import { useState, useEffect } from 'react';
import { NAVY, GOLD, WHITE, fmt, isOver, PRIORITY_COLORS, STATUS_COLORS, STATUSES, PRIORITIES, NOTE_TYPES } from '../lib/ui';
import { Pill, Avatar, Btn, Input, Select, Textarea, Field, Card, Empty, NoteText } from '../lib/ui';
import DocumentSection from './DocumentSection';
import { getDocuments } from '../lib/supabase';

function TaskRow({ task: t, memberName, onOpen, onStatusChange, onDelete }) {
  return (
    <div style={{ background:'#FAFAF8', borderRadius:8, border:'1px solid #E5E0D8', padding:'14px 16px', display:'flex', gap:14, alignItems:'center' }}>
      <div style={{ flex:1, cursor:'pointer' }} onClick={onOpen}>
        <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4, flexWrap:'wrap' }}>
          <span style={{ fontSize:14, fontWeight:600, color:NAVY }}>{t.title}</span>
          <Pill label={t.priority} color={PRIORITY_COLORS[t.priority]} size={11} />
          {isOver(t.deadline) && t.status!=='Completed' && t.status!=='Closed' && <Pill label="Overdue" color="#C0392B" size={10} />}
        </div>
        <div style={{ fontSize:12, color:'#888' }}>
          {(t.assignees||[]).map(memberName).join(', ')} · Due: {fmt(t.deadline)} · {(t.subtasks||[]).length} subtasks
        </div>
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
        <Select value={t.status} onChange={e=>onStatusChange(e.target.value)} style={{ fontSize:11, padding:'4px 8px', width:160 }}>
          {STATUSES.map(s=><option key={s}>{s}</option>)}
        </Select>
        <button onClick={()=>{ if(window.confirm('Delete this task?')) onDelete(); }}
          style={{ background:'none', border:'none', color:'#C0392B', cursor:'pointer', fontSize:20, padding:'0 4px', lineHeight:1 }}>×</button>
      </div>
    </div>
  );
}

export default function MatterDetail({ matter: m, team, updateMatter, deleteMatter, updateTask, deleteTask, setActiveTask, setModal }) {
  const [tab, setTab]       = useState('tasks');
  const [editMode, setEdit] = useState(false);
  const [form, setForm]     = useState(m);
  const [docs, setDocs]     = useState([]);

  useEffect(() => {
    getDocuments('matter', m.id).then(setDocs).catch(console.error);
  }, [m.id]);

  const memberName = id => team.find(t=>t.id===id)?.name || '—';
  const save = () => { updateMatter(form); setEdit(false); };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Header card */}
      <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', padding:'20px 24px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ fontFamily:'Georgia,serif', fontSize:22, fontWeight:700, color:NAVY }}>{m.name}</div>
            <div style={{ fontSize:13, color:'#777', marginTop:4 }}>{m.client}{m.type ? ` · ${m.type}` : ''}</div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {editMode
              ? <><Btn onClick={save}>Save</Btn><Btn variant="ghost" onClick={()=>setEdit(false)}>Cancel</Btn></>
              : <Btn variant="outline" onClick={()=>{ setForm(m); setEdit(true); }}>Edit</Btn>
            }
            <button onClick={()=>{ if(window.confirm('Delete this matter and all its tasks? This cannot be undone.')) deleteMatter(); }}
              style={{ background:'none', border:'1px solid #C0392B', color:'#C0392B', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontSize:12 }}>Delete</button>
          </div>
        </div>

        {editMode ? (
          <div className="grid-3col" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
            <Field label="Status"><Select value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>{STATUSES.map(s=><option key={s}>{s}</option>)}</Select></Field>
            <Field label="Priority"><Select value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</Select></Field>
            <Field label="Deadline"><Input type="date" value={form.deadline||''} onChange={e=>setForm({...form,deadline:e.target.value})} /></Field>
            <Field label="Owner"><Select value={form.owner_id||''} onChange={e=>setForm({...form,owner_id:e.target.value})}><option value="">—</option>{team.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</Select></Field>
            <Field label="Start Date"><Input type="date" value={form.start_date||''} onChange={e=>setForm({...form,start_date:e.target.value})} /></Field>
            <Field label="Client"><Input value={form.client||''} onChange={e=>setForm({...form,client:e.target.value})} /></Field>
            <Field label="Description" col="1 / -1"><Textarea value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})} /></Field>
          </div>
        ) : (
          <div className="grid-4col" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
            {[
              ['Status',   <Pill label={m.status}   color={STATUS_COLORS[m.status]}     />],
              ['Priority', <Pill label={m.priority} color={PRIORITY_COLORS[m.priority]} />],
              ['Owner',    memberName(m.owner_id)],
              ['Deadline', <span style={{ color:isOver(m.deadline)?'#C0392B':'inherit', fontWeight:isOver(m.deadline)?700:400 }}>{fmt(m.deadline)}</span>],
            ].map(([l,v]) => (
              <div key={l}>
                <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:13 }}>{v}</div>
              </div>
            ))}
            {m.description && (
              <div style={{ gridColumn:'1 / -1', fontSize:13, color:'#555', borderTop:'1px solid #F0EDE8', paddingTop:12, lineHeight:1.7 }}>{m.description}</div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid #E5E0D8' }}>
        {['tasks','notes','documents'].map(t => (
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:'10px 22px', border:'none', background:'none', cursor:'pointer', fontFamily:'inherit',
            fontSize:13, fontWeight:tab===t?700:400, color:tab===t?NAVY:'#888',
            borderBottom:tab===t?`2px solid ${GOLD}`:'2px solid transparent'
          }}>
            {t==='tasks'     ? `Tasks (${(m.tasks||[]).length})`
            :t==='notes'     ? `Notes (${(m.matter_notes||[]).length})`
            :                  `Documents (${docs.length})`}
          </button>
        ))}
      </div>

      {/* Tasks tab */}
      {tab==='tasks' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {(m.tasks||[]).length===0 && <Empty text="No tasks yet. Click + Add Task above to create one." />}
          {(m.tasks||[]).map(t => (
            <TaskRow key={t.id} task={t} memberName={memberName}
              onOpen={() => setActiveTask(t.id)}
              onStatusChange={status => updateTask(t.id, { status })}
              onDelete={() => deleteTask(t.id)}
            />
          ))}
        </div>
      )}

      {tab==='documents' && (
        <DocumentSection
          linkedType="matter"
          linkedId={m.id}
          team={team}
          docs={docs}
          onDocsChange={setDocs}
        />
      )}
      {tab==='notes' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {(m.matter_notes||[]).length===0 && <Empty text="No notes yet. Click + Note above to add one." />}
          {(m.matter_notes||[]).map(n => (
            <div key={n.id} style={{ background:WHITE, borderRadius:8, border:'1px solid #E5E0D8', borderLeft:`3px solid ${GOLD}`, padding:'14px 16px' }}>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                <Pill label={n.type} color={GOLD} size={11} />
                <span style={{ fontSize:11, color:'#888' }}>{n.author} · {fmt(n.created_at?.slice(0,10))}</span>
              </div>
              <div style={{ fontSize:13, color:'#333', lineHeight:1.7 }}><NoteText text={n.text} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
